import { defineStore } from 'pinia'
import { data4seoService } from '../services/data4seo'
import { supabase } from '../services/supabase'
import { generateBlogIdeas } from '../services/openai'
import { useSubscriptionStore } from './subscription'

export const useKeywordStore = defineStore('keyword', {
  state: () => ({
    keywords: [],  
    searchHistory: [],
    loading: false,
    error: null
  }),

  getters: {
    seedKeyword: (state) => state.keywords.find(k => k.isSeedKeyword),
    relatedKeywords: (state) => state.keywords.filter(k => !k.isSeedKeyword)
  },

  actions: {
    clearKeywords() {
      this.keywords = []
    },
    
    async analyzeKeyword(seedKeyword, locationCode, languageCode) {
      if (!locationCode || !languageCode) {
        throw new Error('Location code and language code are required')
      }

      // Ensure locationCode is an integer
      const locationCodeInt = parseInt(locationCode, 10)
      if (isNaN(locationCodeInt)) {
        throw new Error('Location code must be a valid integer')
      }

      this.loading = true
      this.error = null
      try {
        // Get current session and user first to fail fast if not authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) throw new Error('Failed to get session')
        if (!session) throw new Error('No active session')

        const { data: { user }, error: userError } = await supabase.auth.getUser()
        if (userError || !user) throw new Error('User not authenticated')

        const subscriptionStore = useSubscriptionStore()
        await subscriptionStore.checkAndResetUsage()
        
        if (!subscriptionStore.canSearch) {
          throw new Error('Search limit reached for your subscription tier')
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Analyzing keyword with:', { seedKeyword, locationCode: locationCodeInt, languageCode })
        }
        const response = await data4seoService.analyzeSeedKeyword(seedKeyword, locationCodeInt, languageCode)
        
        if (!response || !Array.isArray(response)) {
          throw new Error('Invalid API response')
        }

        // Filter and transform the results
        const transformedResult = response
          .filter(keyword => {
            return keyword && 
                   typeof keyword === 'object' && 
                   typeof keyword.term === 'string' && 
                   keyword.term.length > 0
          })
          .map(keyword => ({
            ...keyword,
            isSeedKeyword: keyword.term.toLowerCase() === seedKeyword.toLowerCase(),
            seed_keyword: keyword.term.toLowerCase() === seedKeyword.toLowerCase() ? null : seedKeyword,
            location_code: locationCodeInt,
            language_code: languageCode
          }))

        if (transformedResult.length === 0) {
          throw new Error('No valid keyword data found')
        }

        // Prepare the payloads for upsert and deduplicate them
        const uniqueKeyMap = new Map()
        const payloads = transformedResult.map(keyword => {
          const payload = {
            user_id: user.id,
            term: keyword.term,
            search_volume: keyword.searchVolume,
            cpc: keyword.cpc,
            competition: keyword.difficulty?.competition,
            competition_level: keyword.difficulty?.competitionLevel,
            competition_level_text: keyword.difficulty?.competitionLevelText,
            keyword_difficulty: keyword.difficulty?.keywordDifficulty,
            search_intent_primary: keyword.searchIntent?.primary || null,
            search_intent_secondary: keyword.searchIntent?.secondary || [],
            is_seed_keyword: !!keyword.isSeedKeyword,
            is_opportunity: !!keyword.isOpportunity,
            seed_keyword: keyword.seed_keyword,
            location_code: locationCodeInt,
            language_code: languageCode,
            etv: keyword.serpData?.etv || 0,
            avg_backlinks: keyword.serpData?.avgBacklinks || 0,
            avg_domain_age: keyword.serpData?.avgDomainAge || 0,
            avg_page_score: keyword.serpData?.avgPageScore || 0,
            competitor_count: keyword.serpData?.competitorCount || 0,
            monthly_searches: keyword.monthlySearches || [],
            search_trends: keyword.searchTrends || {},
            updated_at: new Date().toISOString()
          }
          
          // Create a unique key based on the conflict columns
          const uniqueKey = `${user.id}-${keyword.term}-${locationCodeInt}`
          uniqueKeyMap.set(uniqueKey, payload)
          return payload
        })

        // Get unique payloads
        const uniquePayloads = Array.from(uniqueKeyMap.values())

        if (process.env.NODE_ENV === 'development') {
          console.log('Upserting keywords with location code:', locationCodeInt)
        }
        // First try to update existing records
        const { error: updateError } = await supabase
          .from('keywords')
          .upsert(uniquePayloads, {
            onConflict: 'user_id,term,location_code'
          })

        if (updateError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error upserting keywords:', updateError)
          }
          throw updateError
        }

        // Set the new results
        this.keywords = transformedResult

        // Add to search history and increment usage
        await Promise.all([
          this.addToHistory(seedKeyword),
          subscriptionStore.incrementSearchUsage()
        ])

        return transformedResult
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
        console.error('Error analyzing keyword:', error)
        }
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async generateIdeas(keyword, contentType = '', languageCode = 'en') {
      if (!keyword.location_code) {
        throw new Error('Location code is required')
      }

      const locationCodeInt = parseInt(keyword.location_code)
      if (isNaN(locationCodeInt)) {
        throw new Error('Invalid location code')
      }

      try {
        // Get current session and user first to fail fast if not authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) throw new Error('Failed to get session')
        if (!session) throw new Error('No active session')

        const { data: { user }, error: userError } = await supabase.auth.getUser()
        if (userError || !user) throw new Error('User not authenticated')

        const subscriptionStore = useSubscriptionStore()
        await subscriptionStore.checkAndResetUsage()

        if (!subscriptionStore.canCreateContent) {
          throw new Error('Content creation limit reached for your subscription tier')
        }

        // First, get the keyword from the database to ensure we have the correct language code
        const { data: keywordData, error: keywordError } = await supabase
          .from('keywords')
          .select('language_code')
          .eq('user_id', user.id)
          .eq('term', keyword.term)
          .eq('location_code', locationCodeInt)
          .single()

        if (keywordError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error fetching keyword language:', keywordError)
          }
          throw keywordError
        }

        // Use the database language code, or the provided one, or the keyword's one
        const effectiveLanguageCode = keywordData?.language_code || keyword.language_code || languageCode

        if (process.env.NODE_ENV === 'development') {
          console.log('Generating ideas for keyword:', {
            keyword: keyword.term,
            contentType,
            languageCode
          })
        }

        const ideas = await generateBlogIdeas(
          keyword.term,
          keyword.searchVolume,
          keyword.difficulty?.competition || 0,
          contentType,
          keyword.searchIntent,
          effectiveLanguageCode // Pass the effective language code to OpenAI
        )

        // Update the keyword in the database with the new ideas
        const { error: updateError } = await supabase
          .from('keywords')
          .update({
            blog_ideas: ideas, // Use blog_ideas instead of blogIdeas
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('term', keyword.term)
          .eq('location_code', locationCodeInt)

        if (updateError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error updating keyword with ideas:', updateError)
          }
          throw updateError
        }

        // Update the keyword in our local state
        const index = this.keywords.findIndex(k => 
          k.term === keyword.term && 
          k.location_code === locationCodeInt
        )
        if (index !== -1) {
          this.keywords[index] = {
            ...this.keywords[index],
            blog_ideas: ideas
          }
        }

        // Increment content creation usage
        await subscriptionStore.incrementContentCreationUsage()

        return ideas
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error generating ideas:', error)
        }
        throw error
      }
    },

    async loadKeywordsForSeed(seedKeyword) {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        const { data: seedData, error: seedError } = await supabase
          .from('keywords')
          .select('*')
          .eq('user_id', user.id)
          .eq('term', seedKeyword)
          .eq('is_seed_keyword', true)
          .single()

        if (seedError) throw seedError

        const { data: relatedData, error: relatedError } = await supabase
          .from('keywords')
          .select('*')
          .eq('user_id', user.id)
          .eq('seed_keyword', seedKeyword)
          .eq('is_seed_keyword', false)

        if (relatedError) throw relatedError

        const transformedData = [seedData, ...(relatedData || [])].map(item => ({
          term: item.term,
          searchVolume: item.search_volume,
          cpc: item.cpc,
          difficulty: {
            competitionLevel: item.competition_level,
            competitionLevelText: item.competition_level_text,
            competition: item.competition
          },
          searchIntent: {
            primary: item.search_intent_primary,
            secondary: item.search_intent_secondary || []
          },
          serpData: {
            etv: item.etv,
            avgBacklinks: item.avg_backlinks,
            avgDomainAge: item.avg_domain_age,
            avgPageScore: item.avg_page_score,
            competitorCount: item.competitor_count,
            competitorMetrics: {
              avgTitle: item.avg_title_length || 0,
              avgDescription: item.avg_description_length || 0,
              topDomains: item.top_domains || []
            }
          },
          monthlySearches: item.monthly_searches,
          searchTrends: item.search_trends,
          blog_ideas: item.blog_ideas,
          isSeedKeyword: item.is_seed_keyword,
          isOpportunity: item.is_opportunity,
          location_code: item.location_code,
          language_code: item.language_code,
          seed_keyword: item.seed_keyword
        }))

        return transformedData
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
        console.error('Error loading keywords for seed:', error)
        }
        throw error
      }
    },

    async loadSearchHistory() {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        const { data, error } = await supabase
          .from('keywords')
          .select('term, created_at')
          .eq('user_id', user.id)
          .eq('is_seed_keyword', true)
          .order('created_at', { ascending: false })

        if (error) throw error
        this.searchHistory = data
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
        console.error('Error loading search history:', error)
        }
        throw error
      }
    },

    async loadUserKeywords() {
      if (process.env.NODE_ENV === 'development') {
      console.log('🔄 LOADING USER KEYWORDS - START')
        console.log('🔑 Checking Supabase client:', {
          initialized: !!supabase,
          authenticated: !!supabase?.auth?.session
        })
      }
      this.loading = true
      
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        if (process.env.NODE_ENV === 'development') {
        console.log('🔑 Checking Supabase client:', {
          hasClient: !!supabase,
          hasAuth: !!supabase?.auth,
          hasDB: !!supabase?.from
        })
        }

        if (sessionError) {
          if (process.env.NODE_ENV === 'development') {
          console.error('❌ Session error:', sessionError)
          }
          throw new Error('Failed to get session')
        }

        if (!session) {
          if (process.env.NODE_ENV === 'development') {
          console.error('❌ No active session')
          }
          throw new Error('No active session')
        }

        if (process.env.NODE_ENV === 'development') {
        console.log('✅ Session valid:', {
          hasAccessToken: !!session.access_token,
          expiresAt: new Date(session.expires_at * 1000).toISOString()
        })
        }

        const { data: { user }, error: userError } = await supabase.auth.getUser()
        if (userError || !user) {
          console.error('❌ User error:', userError)
          throw new Error('User not authenticated')
        }

        console.log('👤 User authenticated:', {
          id: user.id,
          email: user.email
        })

        // Test RLS with a simple query first
        console.log('🔍 Testing RLS policies...')
        const { data: testData, error: testError } = await supabase
          .from('keywords')
          .select('count')
          .eq('user_id', user.id)

        if (testError) {
          console.error('❌ RLS test failed:', testError)
          throw new Error('Failed to query keywords - RLS test failed')
        }

        console.log('✅ RLS test passed:', {
          count: testData?.[0]?.count
        })

        // Fetch seed keywords
        console.log('🌱 Fetching seed keywords...')
        const { data: seedKeywords, error: seedError } = await supabase
          .from('keywords')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_seed_keyword', true)
          .order('created_at', { ascending: false })

        if (seedError) {
          console.error('❌ Seed keywords error:', seedError)
          throw seedError
        }

        console.log('✅ Seed keywords fetched:', {
          count: seedKeywords?.length || 0
        })

        // Fetch related keywords
        console.log('🔗 Fetching related keywords...')
        const { data: relatedKeywords, error: relatedError } = await supabase
          .from('keywords')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_seed_keyword', false)
          .order('created_at', { ascending: false })

        if (relatedError) {
          console.error('❌ Related keywords error:', relatedError)
          throw relatedError
        }

        console.log('✅ Related keywords fetched:', {
          count: relatedKeywords?.length || 0
        })

        // Combine and transform data
        const allKeywords = [...(seedKeywords || []), ...(relatedKeywords || [])]
        console.log('📊 Processing keywords:', {
          seedCount: seedKeywords?.length || 0,
          relatedCount: relatedKeywords?.length || 0,
          totalCount: allKeywords.length
        })

        const transformedData = allKeywords.map(item => ({
          id: item.id,
          term: item.term,
          searchVolume: item.search_volume,
          keyword_difficulty: item.keyword_difficulty,
          searchIntent: {
            primary: item.search_intent_primary,
            secondary: item.search_intent_secondary || []
          },
          is_seed_keyword: item.is_seed_keyword,
          is_opportunity: item.is_opportunity,
          competition_level: item.competition_level,
          competition_level_text: item.competition_level_text,
          created_at: item.created_at,
          location_code: item.location_code,
          seed_keyword: item.seed_keyword
        }))

        console.log('✅ Data transformation complete:', {
          inputCount: allKeywords.length,
          outputCount: transformedData.length,
          sample: transformedData[0],
          sampleSearchIntent: transformedData[0]?.searchIntent,
          rawSearchIntent: allKeywords[0]?.search_intent_primary
        })

        this.keywords = transformedData
        this.loading = false
      } catch (error) {
        console.error('❌ Error loading user keywords:', error)
        this.error = error.message
        this.loading = false
        throw error
      }
    },

    async fetchUserKeywords() {
      this.loading = true
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        const { data, error } = await supabase
          .from('keywords')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(999999999) // Set a very high limit to effectively get all records

        if (error) throw error

        const transformedData = data.map(item => ({
          term: item.term,
          searchVolume: item.search_volume,
          cpc: item.cpc,
          difficulty: {
            competitionLevel: item.competition_level,
            competitionLevelText: item.competition_level_text,
            competition: item.competition
          },
          searchIntent: {
            primary: item.search_intent_primary,
            secondary: item.search_intent_secondary || []
          },
          serpData: {
            etv: item.etv,
            avgBacklinks: item.avg_backlinks,
            avgDomainAge: item.avg_domain_age,
            avgPageScore: item.avg_page_score,
            competitorCount: item.competitor_count,
            competitorMetrics: {
              avgTitle: item.avg_title_length || 0,
              avgDescription: item.avg_description_length || 0,
              topDomains: item.top_domains || []
            }
          },
          monthlySearches: item.monthly_searches || [],
          searchTrends: item.search_trends || {},
          blog_ideas: item.blog_ideas || [],
          isSeedKeyword: item.is_seed_keyword,
          isOpportunity: item.is_opportunity,
          seed_keyword: item.seed_keyword,
          location_code: item.location_code,
          language_code: item.language_code
        }))

        // Log the first item to verify language_code is present
        if (transformedData.length > 0) {
          console.log('First keyword data:', {
            term: transformedData[0].term,
            language_code: transformedData[0].language_code,
            raw_language_code: data[0].language_code
          })
        }

        this.keywords = transformedData
        return transformedData
      } catch (error) {
        console.error('Error fetching user keywords:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    setKeywords(keywords) {
      this.keywords = keywords
    },

    addToHistory(seedKeyword) {
      const searchEntry = {
        ...seedKeyword,
        id: Date.now(),
        timestamp: new Date().toISOString()
      }

      this.searchHistory.unshift(searchEntry)

      if (this.searchHistory.length > 10) {
        this.searchHistory.pop()
      }

      localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory))
    },

    loadHistory() {
      const savedHistory = localStorage.getItem('searchHistory')
      if (savedHistory) {
        this.searchHistory = JSON.parse(savedHistory)
      }
    }
  }
})
